import { useState } from 'react'

import { useRouter } from 'next/router'

import { LocaleTypes } from '@/types/LocaleTypes'
import * as RadixToast from '@radix-ui/react-toast'
import clsx from 'clsx'

import { getBaseUrl } from '@/lib/contentful/utils'

import { Link } from '@/components/CustomLink'

type Props = {
	preview?: boolean
	duration?: number
	title?: string
	description?: string
	action?: string
	onAction?: () => void
	pageType?: string
}

type Page =
	| 'blog'
	| 'events'
	| 'make-it-big'
	| 'articles'
	| 'case-study'
	| 'press'
	| 'glossary'
	| 'resources'

const EN_LOCALES: Locale[] = [LocaleTypes.en_US, LocaleTypes.en_AU, LocaleTypes.en_GB]
const CEV3_LOCALES: Locale[] = [
	LocaleTypes.da_DK,
	LocaleTypes.de_AT,
	LocaleTypes.sv_SE,
	LocaleTypes.nb_NO
]
const INTL_LOCALES: Locale[] = [
	LocaleTypes.es_ES,
	LocaleTypes.es_MX,
	LocaleTypes.fr_FR,
	LocaleTypes.it_IT,
	LocaleTypes.nl_NL,
	LocaleTypes.de_DE
]

const LOCALES_AVAILABLE: Partial<Record<Page, Locale[]>> = {
	blog: [...EN_LOCALES],
	events: [...EN_LOCALES],
	articles: [...EN_LOCALES, ...INTL_LOCALES],
	resources: [...EN_LOCALES, ...INTL_LOCALES, ...CEV3_LOCALES],
	glossary: [...EN_LOCALES],
	press: [...EN_LOCALES, ...INTL_LOCALES, ...CEV3_LOCALES],
	'case-study': [...EN_LOCALES, ...INTL_LOCALES, ...CEV3_LOCALES]
}

export const PreviewToast = ({
	preview,
	duration = 3600000,
	title = 'Preview Mode',
	description = 'You are currently viewing the preview mode',
	action = 'Stop Preview',
	pageType,
	onAction
}: Props) => {
	const [open, setOpen] = useState(true)
	const { asPath, locale: slug_locale } = useRouter()
	const paths = asPath.split('/').filter(Boolean)
	const page = paths[0] as Page
	const slug = paths.length > 1 ? paths.at(-1) || '' : ''
	// Check if it has an ancestor origin (Makeswift) - if so, hide the toast in Makeswift builder
	const isAncestor = () => {
		if (typeof window === 'undefined') return false
		return Boolean(window?.location?.ancestorOrigins?.length)
	}

	if (!preview) return null

	return (
		<RadixToast.Provider swipeDirection="right" duration={duration}>
			<RadixToast.Root
				className={clsx(
					isAncestor() && 'hidden',
					"bg-gray-1300 rounded-md shadow-2xl drop-shadow-lg p-[15px] grid [grid-template-areas:_'title_action'_'description_action'] grid-cols-[auto_max-content] gap-x-[15px] items-center"
				)}
				open={open}
				onOpenChange={setOpen}
			>
				<RadixToast.Title className="[grid-area:_title] mb-[5px] font-medium text-slate12 text-[15px]">
					{title} for [{slug_locale}]
				</RadixToast.Title>
				<RadixToast.Description asChild>
					<div className="grid [grid-area:_description] gap-4 m-0 text-slate11 text-[13px] leading-[1.3]">
						{description}
						<ul className="grid grid-flow-row gap-2">
							{generateLocaleLinks(page, slug, pageType)}
						</ul>
						<Link href="/api/preview/stop" target="_blank">
							<button className="inline-flex items-center justify-center rounded font-medium text-xs leading-[25px] h-[25px]">
								{action}
							</button>
						</Link>
					</div>
				</RadixToast.Description>
			</RadixToast.Root>
			<RadixToast.Viewport className="[--viewport-padding:_25px] fixed bottom-0 right-0 flex flex-col p-[var(--viewport-padding)] gap-[10px] w-[390px] max-w-[100vw] m-0 list-none z-[2147483647]" />
		</RadixToast.Provider>
	)
}

const generateLocaleLinks = (page: Page, slug: string, pageType?: string) => {
	return LOCALES_AVAILABLE[page]?.map((loc) => {
		const previewUrl = `${getBaseUrl(loc)}/api/preview/start?secret=UlTvQ0xiDh`

		const queryParams = new URLSearchParams({
			page,
			slug,
			locale: loc,
			...(pageType && { type: pageType })
		}).toString()
		const href = `${previewUrl}&${queryParams}`

		return (
			<a
				key={loc}
				href={href}
				target="_blank"
				className="inline-flex rounded font-medium text-xs leading-[25px] h-[25px]"
			>
				Link for {loc} preview
			</a>
		)
	})
}
